// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { useIsMount } from '@Hooks/useIsMount';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PermissionOutput } from 'thehive-sdk';

import './PermissionManager.scss';

interface PermissionManagerProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    permissions: PermissionOutput[];
    isAdmin?: boolean;
}

export const PermissionManager = ({ value, onChange, permissions, isAdmin }: PermissionManagerProps) => {
    const i = useLocalMessage();
    const isMount = useIsMount();

    const [expand, setExpand] = useState(
        permissions
            .map(({ name }) => name)
            .reduce(
                (acc, name) => {
                    if (!name.includes('/')) {
                        return {
                            ...acc,
                            default: false,
                        };
                    }
                    const [section = ''] = name.split('/');

                    return {
                        ...acc,
                        [section]: false,
                    };
                },
                {
                    default: false,
                } as Record<'default' | string, boolean>,
            ),
    );

    const mapTree = useCallback(
        (permissions: string[]) =>
            permissions
                .sort((nameA, nameB) => nameA.localeCompare(nameB))
                .reduce(
                    (acc, name) => {
                        if (!name.includes('/')) {
                            return {
                                ...acc,
                                default: [...acc.default, name],
                            };
                        }
                        const [section = '', role = ''] = name.split('/');

                        return {
                            ...acc,
                            [section]: [...(acc[section] || []), role],
                        };
                    },
                    {
                        default: [],
                    } as Record<'default' | string, string[]>,
                ),
        [],
    );

    const [permTree, setPermTree] = useState<Record<string, string[]>>(value ? mapTree(value) : {});

    const setValues = useCallback(
        (key: string, values: string[]) => {
            setPermTree((prevValue) => {
                const newObj: typeof permTree = {
                    ...prevValue,
                    [key]: values,
                };

                onChange?.(
                    Object.entries(newObj).reduce(
                        (acc, [key, perms]) => [
                            ...acc,
                            ...perms.map((perm) => (key === 'default' ? perm : `${key}/${perm}`)),
                        ],
                        [] as string[],
                    ),
                );

                return newObj;
            });
        },
        [onChange],
    );

    const objPermissions = useMemo(() => mapTree(permissions.map(({ name }) => name)), [permissions, mapTree]);

    useEffect(() => {
        if (!isMount) {
            setPermTree(mapTree([]));
            onChange?.([]);
        }
    }, [isAdmin, mapTree]); // eslint-disable-line

    return (
        <div className="permission-manager">
            <div className="permission-manager__button">
                <THButton
                    type="link"
                    onClick={() => {
                        Object.entries(objPermissions).forEach(([key, perms]) => setValues(key, perms));
                    }}
                >
                    {i('common.select-all')}
                </THButton>{' '}
                /{' '}
                <THButton
                    type="link"
                    onClick={() => {
                        Object.entries(objPermissions).forEach(([key]) => setValues(key, []));
                    }}
                >
                    {i('common.deselect-all')}
                </THButton>
            </div>

            {Object.entries(objPermissions).map(([key, perms]) => {
                if (key !== 'default') {
                    return (
                        <React.Fragment key={key}>
                            <div className="permission-manager__title">
                                <THCheckbox
                                    indeterminate={
                                        perms.length !== permTree?.[key]?.length && !!permTree?.[key]?.length
                                    }
                                    checked={perms.every((perm) => permTree?.[key]?.includes(perm))}
                                    value={key}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setValues(key, perms);
                                        } else {
                                            setValues(key, []);
                                        }
                                    }}
                                >
                                    {i(`profile.permissions.${key}`)}
                                </THCheckbox>

                                <div
                                    className={classList('permission-manager__arrow', { clicked: expand[key] })}
                                    onClick={() => setExpand((prevValue) => ({ ...prevValue, [key]: !prevValue[key] }))}
                                >
                                    <ICONS.TriangleOutlined />
                                </div>
                            </div>
                            <THCheckbox.Group
                                options={perms.map((value) => ({
                                    label: i(`profile.permissions.${key}Permissions.${value}`),
                                    value,
                                }))}
                                onChange={(checkedValue) =>
                                    setValues(key, [...checkedValue.map((value) => value.toString())])
                                }
                                value={permTree[key] || []}
                                className={classList('permission-manager__checkbox-group', { hidden: !expand[key] })}
                            />
                        </React.Fragment>
                    );
                }
                return (
                    <THCheckbox.Group
                        key={key}
                        options={perms.map((value) => ({ label: i(`profile.permissions.${value}`), value }))}
                        onChange={(checkedValue) => setValues(key, [...checkedValue.map((value) => value.toString())])}
                        value={permTree[key] || []}
                    />
                );
            })}
        </div>
    );
};
